/**
 * The function `convertAspectRatio` takes a string representing an aspect ratio and returns it in the
 * format `:`.
 * @param {string} string - The `convertAspectRatio` function takes a string as input and converts it
 * into an aspect ratio format `:`. If the input string does not contain a hyphen
 * '-', it is returned as is. If the string contains a hyphen, it is split into two parts and converted
 * into the format `:`, where the first part is the width and the second part is the height.
 * @returns The function `convertAspectRatio` returns a template string with the aspect ratio in the
 * format `:`. If the input string does not contain a '-', it is directly returned as
 * `:`. If the input string contains a '-', it is split into parts, converted to
 * integers, and then returned as a template string in the `:` format.
 */
export const convertAspectRatio = (string: string): `${number}:${number}` => {
	if (!string.includes('-')) {
		return string as `${number}:${number}`;
	}

	const parts = string.split('-');
	return `${parseInt(parts[0], 10)}:${parseInt(parts[1], 10)}`;
};

/**
 * The function `convertAspectRatioToCustomProp` takes an aspect ratio string and converts it to a CSS custom property value.
 * It converts formats like "4:3" to "4 / 3" for use in CSS custom properties.
 * @param {string} aspectRatio - The aspect ratio string (e.g., "4:3", "16:9")
 * @returns {string} The CSS custom property value (e.g., "4 / 3", "16 / 9")
 */
export const convertAspectRatioToCustomProp = (aspectRatio: string): string => {
	const formattedRatio = convertAspectRatio(aspectRatio);
	const [width, height] = formattedRatio.split(':');
	return `${width} / ${height}`;
};
